import React from 'react';
import { Link } from 'react-router-dom';
import BlogPostLayout from '../BlogPostLayout';
import { H2, H3, StatBarChart, StepDiagram, CompareTable, FeaturedBanner } from '../BlogElements';

const toc = [
  { id: 'introduction', label: 'Introduction' },
  { id: 'what-is', label: 'What Is Shoe-Sole Contamination?' },
  { id: 'types', label: 'Types of Shoe-Sole Contaminants' },
  { id: 'benefits', label: 'Benefits of Addressing It' },
  { id: 'challenges', label: 'Challenges in Reducing It' },
  { id: 'how-it-works', label: 'How Entry-Point Disinfection Works' },
  { id: 'applications', label: 'Where This Matters Most' },
  { id: 'comparison', label: 'Doormats vs. Disinfection' },
  { id: 'statistics', label: 'Statistics & Trends' },
  { id: 'expert-insights', label: 'Expert Insights' },
  { id: 'conclusion', label: 'Conclusion' },
];

const faqItems = [
  { question: 'Do doormats kill bacteria?', answer: 'No. Standard woven or bristle doormats physically trap loose soil and debris, but they have no mechanism to kill bacteria, viruses, or fungal spores, since pathogens on shoe soles largely transfer straight through or onto the mat surface.' },
  { question: 'How dirty are shoe soles, really?', answer: 'Multiple microbiology studies on shoe-sole swabs have found the average outdoor sole carries hundreds of thousands to millions of bacterial colony-forming units, including species like E. coli, associated with fecal contamination picked up from public restrooms, footpaths, and parking lots.' },
  { question: 'Can I just take my shoes off at the door instead?', answer: 'Removing shoes helps, but it depends entirely on consistent behaviour from every household member and every visitor; a single lapse reintroduces the same contamination. Automated disinfection removes that dependency on behaviour.' },
  { question: 'Are shoe soles really a meaningful transmission risk compared to hands?', answer: 'Hands are cleaned far more frequently than shoe soles in most households. Research on fomite transmission has shown shoe soles can carry and redistribute pathogens across floors, which are then picked up by hands, pets, and crawling children.' },
  { question: 'What is the difference between cleaning a mat and disinfecting shoe soles?', answer: 'Cleaning a mat (vacuuming or washing it) removes what has already accumulated on the mat. Disinfecting shoe soles intervenes before contamination is deposited indoors at all, which is the approach SaniQ is built around.' },
  { question: 'Is this only a concern in wet or muddy conditions?', answer: 'No, dry contamination (dust-borne bacteria, viruses on hard-soled shoes) transfers just as readily, if less visibly, as mud or moisture.' },
];

const references = [
  { label: 'CDC, Handwashing: A Family Activity (fomite & hand hygiene overview)', url: 'https://www.cdc.gov/handwashing/index.html' },
  { label: 'World Health Organization, Hand Hygiene and Infection Prevention', url: 'https://www.who.int/teams/integrated-health-services/infection-prevention-control/hand-hygiene' },
  { label: 'U.S. EPA, List N: Disinfectants for Use Against Pathogens', url: 'https://www.epa.gov/pesticide-registration/list-n-disinfectants-coronavirus-covid-19' },
];

const relatedLinks = [
  { to: '/blog/smart-vs-traditional-doormat', label: 'Smart Disinfectant Doormat vs. Traditional Doormat', type: 'Blog' },
  { to: '/blog/uv-vs-spray-disinfection', label: 'UV Sanitizer vs. Spray Disinfection for Entryways', type: 'Blog' },
  { to: '/features', label: "How SaniQ's Smart Disinfectant Doormat Works", type: 'Product' },
  { to: '/waitlist', label: 'Join the Waitlist for 2027 Launch', type: 'Get Started' },
];

const DoDoormatsKillBacteria = () => (
  <BlogPostLayout
    slug="do-doormats-kill-bacteria"
    category="Hygiene Science"
    title="Do Doormats Kill Bacteria? What Shoe-Sole Studies Actually Show"
    description="Do doormats kill bacteria? A look at what shoe-sole microbiology studies actually find, why 96% of shoes carry harmful bacteria, and what it takes to actually disinfect an entryway."
    datePublished="2027-03-02"
    readTime="11 min read"
    toc={toc}
    faqItems={faqItems}
    relatedLinks={relatedLinks}
    references={references}
  >
    <FeaturedBanner
      eyebrow="Quick Answer"
      headline="No, traditional doormats trap visible dirt but do not kill bacteria, viruses, or fungal spores on shoe soles."
      label="Featured summary graphic: standard doormats do not kill bacteria"
    />

    <H2 id="introduction">Introduction</H2>
    <p>
      It's a question most people never think to ask, because the answer feels obvious: of course the doormat is doing something. It's right there, catching the mud, the grit, the wet footprints. But "catching debris" and "killing bacteria" are two entirely different jobs, and only one of them is what a standard doormat is built for. The short answer is that <strong>doormats do not kill bacteria</strong>, and the shoe-sole studies behind that answer are more striking than most people expect.
    </p>
    <p>
      This matters more than it sounds like it should. Shoe soles are one of the few surfaces in a home or workplace that make direct, repeated contact with public ground, including footpaths, public restrooms, parking lots, hospital floors, and transit stations, and then get carried straight across every indoor floor a person walks on. Understanding what's actually happening at that transfer point is the first step to closing the gap.
    </p>

    <H2 id="what-is">What Is Shoe-Sole Contamination?</H2>
    <H3>Definition</H3>
    <p>
      Shoe-sole contamination refers to the accumulation of bacteria, viruses, fungal spores, and particulate matter (dust, pollen, chemical residue) on the outsole of footwear through contact with public and outdoor surfaces. Unlike hand contamination, which is addressed dozens of times a day through washing, shoe-sole contamination typically goes untouched between wears.
    </p>
    <H3>How It Works</H3>
    <p>
      Every surface a shoe contacts, whether a footpath, a public restroom floor, or a supermarket aisle, deposits some quantity of microorganisms onto the tread. Textured soles are particularly effective at this: the grooves designed for grip also trap and hold organic matter, moisture, and microbes far longer than a flat surface would. Each subsequent step then transfers a portion of that load onto the next surface, including a home or office floor.
    </p>
    <H3>Why It Matters</H3>
    <p>
      A traditional entry mat interrupts almost none of this cycle. It flattens visible mud and absorbs some moisture, but it has no antimicrobial mechanism, meaning bacteria deposited onto the mat can persist and even transfer back onto the next shoe or bare foot that touches it. The mat becomes a reservoir rather than a barrier.
    </p>

    <StatBarChart
      label="Share of shoe soles carrying harmful bacteria vs. pathogens eliminated by SaniQ's automated disinfection cycle"
      bars={[
        { label: 'Shoes w/ bacteria', value: 96 },
        { label: 'SaniQ elimination', value: 99.9 },
      ]}
    />

    <H2 id="types">Types of Shoe-Sole Contaminants</H2>
    <H3>Bacterial Contamination</H3>
    <p>Includes species such as E. coli, Klebsiella pneumoniae, and Staphylococcus aureus, commonly identified in shoe-sole swab studies and typically linked to fecal, soil, or public-restroom sources.</p>
    <H3>Viral Contamination</H3>
    <p>Enveloped and non-enveloped viruses can survive on hard shoe-sole surfaces for extended periods, particularly in cooler, low-light conditions typical of entryways.</p>
    <H3>Fungal & Mold Spores</H3>
    <p>Damp conditions and organic debris on soles create a viable environment for fungal spore transport, a factor relevant to both allergy sufferers and general indoor air quality.</p>
    <H3>Particulate & Chemical Residue</H3>
    <p>Beyond microorganisms, soles carry pollen, heavy-metal dust from road surfaces, and chemical residues (de-icing salt, pesticide runoff, industrial dust) that a mat also fails to neutralize.</p>

    <H2 id="benefits">Benefits of Addressing Shoe-Sole Contamination</H2>
    <H3>Reduced Indoor Pathogen Load</H3>
    <p>Intervening at the entry point reduces the baseline level of bacteria and viruses distributed across indoor floors before any other cleaning happens.</p>
    <H3>Lower Cross-Contamination to Hands and Surfaces</H3>
    <p>Floors are touched indirectly by hands (via pets, dropped items, crawling infants) far more often than most people account for.</p>
    <H3>Support for Vulnerable Groups</H3>
    <p>Young children, elderly household members, and immunocompromised individuals face disproportionate risk from indoor pathogen exposure.</p>
    <H3>Allergen Reduction</H3>
    <p>Intercepting pollen and particulate matter at the door reduces the allergen load that circulates through HVAC systems and settles on soft furnishings.</p>
    <H3>Fewer Manual Cleaning Cycles</H3>
    <p>Addressing contamination at the source reduces how often floors need re-mopping or disinfecting deeper in the home.</p>

    <H2 id="challenges">Challenges in Reducing Shoe-Sole Contamination</H2>
    <H3>Behavioural Inconsistency</H3>
    <p>Shoes-off policies and mat-wiping habits depend on every household member and visitor complying, every time; a single exception undermines the whole system.</p>
    <H3>Limited Awareness</H3>
    <p>Most people simply haven't been shown the data on what's actually on a shoe sole, so the entry point isn't treated as a hygiene priority.</p>
    <H3>Mat Maintenance Burden</H3>
    <p>Traditional mats need regular washing or replacement to avoid becoming a contamination reservoir themselves, a step frequently skipped.</p>
    <H3>No Passive Solution Exists</H3>
    <p>Until automated disinfection technology, there was no passive (no-effort) way to intervene, only active steps like shoe removal or manual sole wiping, both behaviour-dependent.</p>

    <H2 id="how-it-works">How Entry-Point Disinfection Works</H2>
    <StepDiagram
      label="How an automated disinfectant doormat like SaniQ intercepts shoe-sole contamination"
      steps={[
        'Pressure sensors detect a shoe stepping onto the mat',
        'Sensors identify the exact contact area of the sole',
        'Medical-grade disinfectant is released precisely at that contact point',
        'The disinfection cycle completes in 2-3 seconds',
        'The system logs the cycle and monitors disinfectant levels',
      ]}
    />
    <p>
      This is the core distinction from a traditional mat: intervention happens automatically, at the exact moment of contact, without requiring a person to remember to do anything. For a full technical breakdown, see <Link to="/features" className="text-cyan-400 hover:text-cyan-300 transition-colors">how SaniQ's disinfection technology works</Link>.
    </p>

    <H2 id="applications">Where This Matters Most</H2>
    <H3>Family Homes</H3>
    <p>Households with young children face the highest exposure risk from floor-level contamination, given how much time infants and toddlers spend at ground level.</p>
    <H3>Aged Care & Elderly Households</H3>
    <p>Reduced immune resilience makes entry-point hygiene a meaningfully higher priority for elderly residents.</p>
    <H3>Clinics & Healthcare Settings</H3>
    <p>Entryway contamination control is a documented gap in many infection-prevention protocols that focus heavily on hands and surfaces but not footwear.</p>
    <H3>Childcare Facilities</H3>
    <p>High-touch, floor-level environments amplify the consequences of unaddressed shoe-sole contamination.</p>
    <H3>Offices & Shared Workplaces</H3>
    <p>Shared entryways compound individual contamination across an entire building's foot traffic.</p>

    <H2 id="comparison">Doormats vs. Disinfection: A Direct Comparison</H2>
    <H3>Traditional Doormat vs. Automated Disinfection</H3>
    <p>A traditional doormat is a passive filter; an automated disinfection mat is an active intervention. The difference shows up clearly when the mechanisms are placed side by side.</p>
    <H3>Pros &amp; Cons</H3>
    <p>Traditional mats are inexpensive and require no power, but provide no antimicrobial action and can become contamination reservoirs themselves. Automated disinfection mats require upfront investment and periodic refills, but directly address the pathogen load rather than just the visible mess.</p>
    <H3>Feature Comparison Table</H3>
    <CompareTable
      label="Feature comparison: traditional doormat vs. automated disinfection mat"
      columns={['Feature', 'Traditional Doormat', 'Automated Disinfection Mat']}
      rows={[
        ['Traps visible debris', 'Yes', 'Yes'],
        ['Kills bacteria/viruses', 'No', 'Yes'],
        ['Requires behaviour change', 'Yes (wiping feet)', 'No'],
        ['Becomes a contamination reservoir', 'Yes, if unwashed', 'No, self-disinfecting cycle'],
        ['Monitoring/maintenance alerts', 'None', 'Available (e.g. SaniQ)'],
      ]}
    />

    <H2 id="statistics">Statistics &amp; Industry Trends</H2>
    <H3>Latest Statistics</H3>
    <p>Shoe-sole microbiology research consistently finds the large majority of soles tested carry detectable harmful bacteria, a figure commonly cited around 96% across public-space samples.</p>
    <H3>Market Growth</H3>
    <p>Consumer and commercial interest in connected home-hygiene devices has grown steadily since 2020, as awareness of surface and fomite transmission increased across households and businesses alike.</p>
    <H3>Future Trends</H3>
    <p>Expect entry-point hygiene devices to move from novelty to expected infrastructure in healthcare, childcare, and hospitality settings, alongside continued residential adoption.</p>

    <H2 id="expert-insights">Expert Insights</H2>
    <H3>Practical Recommendations</H3>
    <p>Treat every external entrance, not just the front door, as a contamination point, including garage and side entries that are easy to overlook.</p>
    <H3>Common Mistakes to Avoid</H3>
    <p>Assuming a clean-looking mat means a hygienic one; skipping regular mat washing; relying entirely on shoes-off policies without a backup.</p>
    <H3>Industry Standards</H3>
    <p>Relevant hygiene and infection-control standards referenced by commercial adopters will be detailed here ahead of SaniQ&apos;s launch.</p>
    <H3>Compliance &amp; Security</H3>
    <p>Data-handling practices for connected monitoring features will be documented ahead of launch; see our <Link to="/privacy" className="text-cyan-400 hover:text-cyan-300 transition-colors">Privacy Policy</Link>.</p>

    <H2 id="conclusion">Conclusion</H2>
    <p>
      The evidence is straightforward: doormats don't kill bacteria, because they were never built to. They're a mechanical filter for visible mess, not a disinfection system. Given how consistently shoe-sole studies find harmful bacteria on the majority of footwear, closing that gap requires something that actively intervenes, automatically and at the point of contact, every time.
    </p>
  </BlogPostLayout>
);

export default DoDoormatsKillBacteria;
